import { useCallback, useEffect, useRef } from 'react';
import {
  UserCheck,
  UserMinus,
  Shield,
  FileText,
  CircleCheck,
  GitBranch,
  Package,
  Activity,
} from 'lucide-react';
import type { ProjectActivityEvent } from '@/lib/types';
import { formatDateOnly } from '@/lib/date-utils';

const ICON_MAP: Record<string, React.ElementType> = {
  'user-check': UserCheck,
  'user-minus': UserMinus,
  shield: Shield,
  'file-text': FileText,
  'circle-check': CircleCheck,
  'git-branch': GitBranch,
  package: Package,
};

const TYPE_COLORS: Record<string, string> = {
  crew_checkin: 'bg-green-100 text-green-700',
  crew_checkout: 'bg-gray-100 text-gray-600',
  jsa_status: 'bg-blue-100 text-blue-700',
  invoice_sent: 'bg-indigo-100 text-indigo-700',
  invoice_paid: 'bg-emerald-100 text-emerald-700',
  change_order_proposed: 'bg-yellow-100 text-yellow-700',
  change_order_approved: 'bg-green-100 text-green-700',
  deployment_checkout: 'bg-orange-100 text-orange-700',
  deployment_checkin: 'bg-teal-100 text-teal-700',
  gear_request: 'bg-purple-100 text-purple-700',
};

function formatRelativeTime(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDateOnly(iso.slice(0, 10), '');
}

interface ActivityTimelineProps {
  events: ProjectActivityEvent[];
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
}

export function ActivityTimeline({
  events,
  hasMore,
  isLoadingMore,
  onLoadMore,
}: ActivityTimelineProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && hasMore && !isLoadingMore) {
        onLoadMore();
      }
    },
    [hasMore, isLoadingMore, onLoadMore],
  );

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleIntersect, {
      rootMargin: '200px',
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleIntersect]);

  if (events.length === 0 && !isLoadingMore) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Activity className="h-8 w-8 mx-auto mb-3 text-gray-400" />
        <p className="font-medium">No activity yet</p>
        <p className="text-sm">Events will appear here as work progresses.</p>
      </div>
    );
  }

  return (
    <div className="flow-root">
      <ul className="-mb-8">
        {events.map((event, idx) => {
          const Icon = ICON_MAP[event.icon] ?? Activity;
          const colorClass = TYPE_COLORS[event.type] ?? 'bg-gray-100 text-gray-600';
          const isLast = idx === events.length - 1 && !hasMore;

          return (
            <li key={`${event.type}-${event.timestamp}-${idx}`}>
              <div className="relative pb-8">
                {!isLast && (
                  <span
                    className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200"
                    aria-hidden="true"
                  />
                )}
                <div className="relative flex space-x-3">
                  <div>
                    <span
                      className={`flex h-8 w-8 items-center justify-center rounded-full ${colorClass}`}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                  </div>
                  <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1">
                    <div>
                      <p className="text-sm text-gray-900">{event.headline}</p>
                      {event.detail && (
                        <p className="mt-0.5 text-xs text-gray-500">{event.detail}</p>
                      )}
                    </div>
                    <div className="whitespace-nowrap text-right text-xs text-gray-500">
                      {formatRelativeTime(event.timestamp)}
                    </div>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Infinite-scroll sentinel */}
      <div ref={sentinelRef} className="h-1" />

      {isLoadingMore && (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
        </div>
      )}
    </div>
  );
}
